/**
 * PRO-4 step 4 — Media (docs/06 §5.3, §10).
 *
 * Positional photo slots (as many as `GET /config` allows, 12 today). The first six carry
 * advisory side hints (front/back/left/right/top/bottom), the rest read "additional" — "labels
 * advisory", per the spec, so they are hints and never validation. One optional video,
 * drag-drop, per-file progress, per-file error with retry, and drag-to-reorder.
 *
 * Client-side type/size checks MIRROR the server limits from `GET /config` rather than
 * hardcoding them — the whole point of that endpoint is that the widgets cannot drift from the
 * server's authoritative rules. They are a courtesy that saves a doomed 10 MB upload, not a
 * security control: the server re-validates, re-encodes every image (which strips EXIF, so a
 * photo's GPS tags can never leak the factory's address), and MIME-sniffs.
 *
 * Media uploads go straight to the API against a real product id — which is why the wizard
 * creates the draft BEFORE this step is reachable.
 */
import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AlertCircle, GripVertical, ImagePlus, Trash2, Video } from 'lucide-react';
import { Badge, Button, ConfirmDialog, cn, useToast } from '@/components/ui';
import * as api from '@/shared/api';
import { qk } from '@/shared/queryClient';
import { ApiError } from '@/shared/http';
import { useConfig } from '@/shared/session';
import type { Media } from '@/shared/contract';

const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const VIDEO_TYPES = ['video/mp4'];

/**
 * Advisory hints for slots 1–6; later slots read "additional". NOT validated — a producer may
 * put any side in any slot.
 */
const SLOT_HINT_KEYS = [
  'wizard.slotFront',
  'wizard.slotBack',
  'wizard.slotLeft',
  'wizard.slotRight',
  'wizard.slotTop',
  'wizard.slotBottom',
] as const;

interface UploadState {
  position: number;
  progress: number;
  error?: string;
  file: File;
}

export function MediaStep({ productId, media }: { productId: string; media: Media[] }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data: config } = useConfig();

  const maxPhotos = config?.max_photos_per_product ?? 12;
  const maxPhotoBytes = config?.max_photo_bytes ?? 10 * 1024 * 1024;
  const maxVideoBytes = config?.max_video_bytes ?? 100 * 1024 * 1024;

  const photos = React.useMemo(
    () =>
      media
        .filter((m) => m.kind === 'photo')
        .slice()
        .sort((a, b) => (a.position ?? 99) - (b.position ?? 99)),
    [media],
  );
  const video = media.find((m) => m.kind === 'video');

  // In-flight uploads, keyed by slot. Survives failures so the retry button has the File.
  const [uploads, setUploads] = React.useState<Record<number, UploadState>>({});
  const [videoUpload, setVideoUpload] = React.useState<UploadState | null>(null);
  const [dragOverSlot, setDragOverSlot] = React.useState<number | null>(null);
  const [deleting, setDeleting] = React.useState<Media | null>(null);
  const [draggingId, setDraggingId] = React.useState<string | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: qk.product(productId) });

  const mb = (bytes: number) => `${Math.round(bytes / 1024 / 1024)} MB`;

  // ── Upload ────────────────────────────────────────────────────────────────────────────────
  const doUploadPhoto = React.useCallback(
    async (file: File, position: number) => {
      setUploads((u) => ({ ...u, [position]: { position, progress: 0, file } }));

      try {
        await api.uploadProductMedia(productId, file, 'photo', position, (pct) => {
          setUploads((u) =>
            u[position] ? { ...u, [position]: { ...u[position], progress: pct } } : u,
          );
        });

        setUploads((u) => {
          const next = { ...u };
          delete next[position];
          return next;
        });
        void refresh();
      } catch (err) {
        // Keep the entry so the user can retry with the same File — re-picking it from disk after
        // every transient failure is exactly the kind of thing that makes people give up.
        const message =
          err instanceof ApiError ? t(`errors.${err.suffix}`) : t('wizard.uploadFailed');
        setUploads((u) => ({
          ...u,
          [position]: { position, progress: 0, file, error: message },
        }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productId, t],
  );

  const doUploadVideo = React.useCallback(
    async (file: File) => {
      setVideoUpload({ position: 0, progress: 0, file });
      try {
        await api.uploadProductMedia(productId, file, 'video', undefined, (pct) => {
          setVideoUpload((v) => (v ? { ...v, progress: pct } : v));
        });
        setVideoUpload(null);
        void refresh();
      } catch (err) {
        const message =
          err instanceof ApiError ? t(`errors.${err.suffix}`) : t('wizard.uploadFailed');
        setVideoUpload({ position: 0, progress: 0, file, error: message });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productId, t],
  );

  /** Validate against the SERVER's limits, then upload. */
  const acceptPhoto = (file: File | undefined, position: number) => {
    if (!file) return;
    if (!PHOTO_TYPES.includes(file.type)) {
      toast(t('wizard.badType'), 'error');
      return;
    }
    if (file.size > maxPhotoBytes) {
      toast(t('wizard.tooLarge', { size: mb(maxPhotoBytes) }), 'error');
      return;
    }
    void doUploadPhoto(file, position);
  };

  const acceptVideo = (file: File | undefined) => {
    if (!file) return;
    if (!VIDEO_TYPES.includes(file.type)) {
      toast(t('wizard.badType'), 'error');
      return;
    }
    if (file.size > maxVideoBytes) {
      toast(t('wizard.tooLarge', { size: mb(maxVideoBytes) }), 'error');
      return;
    }
    void doUploadVideo(file);
  };

  // ── Reorder ───────────────────────────────────────────────────────────────────────────────
  const reorder = useMutation({
    mutationFn: ({ mediaId, position }: { mediaId: string; position: number }) =>
      api.reorderProductMedia(productId, mediaId, position),
    onSuccess: () => void refresh(),
    onError: (err) => {
      toast(err instanceof ApiError ? t(`errors.${err.suffix}`) : t('common.errorGeneric'), 'error');
    },
  });

  // ── Delete ────────────────────────────────────────────────────────────────────────────────
  const remove = useMutation({
    mutationFn: (mediaId: string) => api.deleteProductMedia(productId, mediaId),
    onSuccess: () => {
      setDeleting(null);
      void refresh();
    },
    onError: (err) => {
      setDeleting(null);
      // The server refuses to let an ACTIVE product lose its last photo (openapi: 422).
      if (err instanceof ApiError && err.status === 422) {
        toast(t('wizard.photoDeleteLast'), 'error');
        return;
      }
      toast(t('common.errorGeneric'), 'error');
    },
  });

  const photoAt = (position: number) => photos.find((p) => p.position === position);

  return (
    <div className="space-y-8">
      {/* ── Photos ── */}
      <section>
        <h3 className="text-sm font-medium text-foreground">{t('wizard.photos')}</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t('wizard.photosHint', { max: maxPhotos, size: mb(maxPhotoBytes) })}
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground/70">{t('wizard.slotHintAdvisory')}</p>

        <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3">
          {Array.from({ length: maxPhotos }, (_, i) => i + 1).map((position) => {
            const photo = photoAt(position);
            const upload = uploads[position];
            const hint = t(SLOT_HINT_KEYS[position - 1] ?? 'wizard.slotExtra');

            return (
              <PhotoSlot
                key={position}
                position={position}
                hint={hint}
                photo={photo}
                upload={upload}
                dragOver={dragOverSlot === position}
                dragging={draggingId}
                onDragOverSlot={(over) => setDragOverSlot(over ? position : null)}
                onFile={(file) => acceptPhoto(file, position)}
                onRetry={() => upload && void doUploadPhoto(upload.file, position)}
                onDismissError={() =>
                  setUploads((u) => {
                    const next = { ...u };
                    delete next[position];
                    return next;
                  })
                }
                onDelete={() => photo && setDeleting(photo)}
                onDragStartPhoto={(id) => setDraggingId(id)}
                onDragEndPhoto={() => setDraggingId(null)}
                onDropPhoto={(mediaId) => {
                  setDraggingId(null);
                  // Dropping a photo onto a slot moves it there (the server resolves the swap;
                  // a 409 means the slot is taken and it tells us so).
                  if (mediaId) reorder.mutate({ mediaId, position });
                }}
              />
            );
          })}
        </div>
      </section>

      {/* ── Video ── */}
      <section>
        <h3 className="text-sm font-medium text-foreground">{t('wizard.video')}</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t('wizard.videoHint', { size: mb(maxVideoBytes) })}
        </p>

        <div className="mt-3">
          {video ? (
            <div className="flex items-center gap-3 rounded-md border border-border bg-card p-3">
              <Video className="h-5 w-5 text-muted-foreground/70" aria-hidden />
              <a
                href={video.url}
                target="_blank"
                rel="noreferrer"
                className="flex-1 truncate text-sm text-link hover:underline"
              >
                {video.url.split('/').pop()}
              </a>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setDeleting(video)}
                aria-label={t('common.delete')}
              >
                <Trash2 className="h-4 w-4 text-muted-foreground/70" aria-hidden />
              </Button>
            </div>
          ) : videoUpload ? (
            <UploadProgress
              state={videoUpload}
              onRetry={() => void doUploadVideo(videoUpload.file)}
              onDismiss={() => setVideoUpload(null)}
            />
          ) : (
            <FilePicker
              accept={VIDEO_TYPES.join(',')}
              onFile={acceptVideo}
              label={t('wizard.dropHere')}
              sublabel={t('wizard.orPick')}
              icon={Video}
            />
          )}
        </div>
      </section>

      <ConfirmDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting.id)}
        loading={remove.isPending}
        title={t('wizard.photoDeleteConfirm')}
        confirmLabel={t('common.delete')}
        destructive
      />
    </div>
  );
}

// ── One photo slot ──────────────────────────────────────────────────────────────────────────
function PhotoSlot({
  position,
  hint,
  photo,
  upload,
  dragOver,
  dragging,
  onDragOverSlot,
  onFile,
  onRetry,
  onDismissError,
  onDelete,
  onDragStartPhoto,
  onDragEndPhoto,
  onDropPhoto,
}: {
  position: number;
  hint: string;
  photo: Media | undefined;
  upload: UploadState | undefined;
  dragOver: boolean;
  dragging: string | null;
  onDragOverSlot: (over: boolean) => void;
  onFile: (file: File | undefined) => void;
  onRetry: () => void;
  onDismissError: () => void;
  onDelete: () => void;
  onDragStartPhoto: (mediaId: string) => void;
  onDragEndPhoto: () => void;
  onDropPhoto: (mediaId: string | null) => void;
}) {
  const { t } = useTranslation();
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    onDragOverSlot(false);

    // Two kinds of drop land here: a FILE from the desktop (upload), or an existing PHOTO being
    // dragged between slots (reorder). They are distinguished by the custom drag payload.
    const mediaId = e.dataTransfer.getData('application/x-most-media');
    if (mediaId) {
      onDropPhoto(mediaId);
      return;
    }

    const file = e.dataTransfer.files?.[0];
    if (file) onFile(file);
  };

  return (
    <div
      className={cn(
        'relative aspect-[4/3] overflow-hidden rounded-lg border-2 border-dashed transition-colors',
        dragOver ? 'border-primary bg-primary-muted' : 'border-input bg-card',
        photo && 'border-solid',
      )}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOverSlot(true);
      }}
      onDragLeave={() => onDragOverSlot(false)}
      onDrop={handleDrop}
    >
      {photo ? (
        <div
          className={cn('group relative h-full w-full', dragging === photo.id && 'opacity-40')}
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData('application/x-most-media', photo.id);
            e.dataTransfer.effectAllowed = 'move';
            onDragStartPhoto(photo.id);
          }}
          onDragEnd={onDragEndPhoto}
        >
          <img
            src={photo.thumb_url ?? photo.url}
            alt={`${t('wizard.photoSlot', { n: position })} — ${hint}`}
            loading="lazy"
            className="h-full w-full object-cover"
          />

          <div className="absolute left-2 top-2 flex items-center gap-1">
            <Badge tone="neutral">
              <GripVertical className="h-3 w-3" aria-hidden />
              {position}
            </Badge>
          </div>

          <button
            type="button"
            onClick={onDelete}
            className="absolute right-2 top-2 rounded-md bg-card/90 p-1.5 text-muted-foreground opacity-0 shadow transition-opacity hover:text-red-600 focus-visible:opacity-100 group-hover:opacity-100"
            aria-label={t('common.delete')}
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      ) : upload ? (
        <div className="flex h-full items-center justify-center p-3">
          <UploadProgress state={upload} onRetry={onRetry} onDismiss={onDismissError} compact />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex h-full w-full flex-col items-center justify-center gap-1 text-muted-foreground/70 transition-colors hover:bg-muted/60 hover:text-foreground"
        >
          <ImagePlus className="h-6 w-6" aria-hidden />
          <span className="text-xs font-medium">{t('wizard.photoSlot', { n: position })}</span>
          {/* Advisory only. */}
          <span className="text-[11px] text-muted-foreground/70">{hint}</span>
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={PHOTO_TYPES.join(',')}
        className="sr-only"
        onChange={(e) => onFile(e.target.files?.[0])}
      />
    </div>
  );
}

// ── Progress / error / retry ────────────────────────────────────────────────────────────────
function UploadProgress({
  state,
  onRetry,
  onDismiss,
  compact = false,
}: {
  state: UploadState;
  onRetry: () => void;
  onDismiss: () => void;
  compact?: boolean;
}) {
  const { t } = useTranslation();

  if (state.error) {
    return (
      <div className="w-full space-y-2 text-center">
        <AlertCircle className="mx-auto h-5 w-5 text-red-500" aria-hidden />
        <p className="text-xs text-red-700">{state.error}</p>
        <div className="flex justify-center gap-2">
          <Button type="button" size="sm" variant="outline" onClick={onRetry}>
            {t('wizard.uploadRetry')}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={onDismiss}>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('w-full space-y-2', !compact && 'rounded-md border border-border p-3')}>
      <p className="truncate text-xs text-muted-foreground">{state.file.name}</p>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full bg-primary transition-[width] duration-150"
          style={{ width: `${state.progress}%` }}
          role="progressbar"
          aria-valuenow={state.progress}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
      <p className="text-right text-[11px] tabular-nums text-muted-foreground">{state.progress}%</p>
    </div>
  );
}

// ── Drop zone / file picker ─────────────────────────────────────────────────────────────────
function FilePicker({
  accept,
  onFile,
  label,
  sublabel,
  icon: Icon,
}: {
  accept: string;
  onFile: (file: File | undefined) => void;
  label: string;
  sublabel: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [over, setOver] = React.useState(false);

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed p-6 transition-colors',
        over ? 'border-primary bg-primary-muted' : 'border-input bg-card',
      )}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        onFile(e.dataTransfer.files?.[0]);
      }}
    >
      <Icon className="h-6 w-6 text-muted-foreground/70" aria-hidden />
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="text-xs text-link hover:underline"
      >
        {sublabel}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="sr-only"
        onChange={(e) => onFile(e.target.files?.[0])}
      />
    </div>
  );
}
